import getCustomerBrandByKey from "../features/branding/utils/getCustomerBrandByKey";
import getCustomerFromSubdomain from "../features/branding/utils/getCustomerFromSubdomain";
import getCustomerKeyFromUrl from "../features/branding/utils/getCustomerKeyFromUrl";
import defaultCustomerLogo from "../assets/odp.png";
import { getApiBaseUrl } from "../config/apiBaseUrl";
import { getPlatformHosts, getSuperAdminHosts } from "../config/platformHosts";

const CUSTOMER_DATA_KEY = "customer_data";

function isPlatformHostNoCustomer(hostname, customerKey) {
  const h = (hostname || "").toLowerCase();
  const platformHosts = getPlatformHosts();
  const superAdminHosts = getSuperAdminHosts();
  if (superAdminHosts.some((p) => h === p)) return true;
  if (!platformHosts.some((p) => h === p)) return false;
  return !customerKey;
}

/** Build brand shape from customer data (API response or parsed localStorage). */
function brandFromCustomerData(customerData) {
  if (!customerData?.name) return null;
  const customerId = customerData.customer_id;
  const staticBrand = getCustomerBrandByKey(customerData.slug || customerId);
  return {
    name: customerData.name,
    logoSrc: customerData.logo_url || staticBrand.logoSrc,
    alt: (customerData.slug || customerId || "").toUpperCase(),
    product_alias: `${customerData.name} Cloud`,
  };
}

function normalizeCustomerPayload(payload, customerKey) {
  if (payload && typeof payload === "object" && !Array.isArray(payload) && payload.name) {
    return payload;
  }

  const list = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.customers)
      ? payload.customers
      : [];
  if (!list.length) return null;

  const key = String(customerKey || "").trim().toLowerCase();
  if (!key) return list[0];

  return list.find((candidate) => {
    const slug = String(candidate?.slug || "").trim().toLowerCase();
    const id = String(candidate?.customer_id || candidate?.customerId || "").trim().toLowerCase();
    return slug === key || id === key;
  }) || list[0];
}

/** Return cached brand from localStorage for instant display on refresh. */
export function getStoredBrand() {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(CUSTOMER_DATA_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    return brandFromCustomerData(data);
  } catch {
    return null;
  }
}

export default async function fetchCustomerBranding() {
  try {
    const hostname = typeof window !== "undefined" ? window.location.hostname : "";
    const customerFromPath = typeof getCustomerFromSubdomain === "function" ? getCustomerFromSubdomain() : null;
    const customerKey = typeof getCustomerKeyFromUrl === "function" ? getCustomerKeyFromUrl({ customerFromPath }) : null;
    if (isPlatformHostNoCustomer(hostname, customerKey)) {
      return { name: "One Door Platform", logoSrc: defaultCustomerLogo, alt: "ODP", product_alias: "ODP Cloud" };
    }
    const apiBaseUrl = getApiBaseUrl();
    const base = apiBaseUrl ? `${apiBaseUrl}/api/v1/customers` : "/api/v1/customers";
    const url = customerKey ? `${base}?slug=${encodeURIComponent(customerKey)}` : base;
    const response = await fetch(url, {
      headers: {
        "X-Original-Host": hostname
      }
    });

    if (response.ok) {
      const payload = await response.json();
      const customerData = normalizeCustomerPayload(payload, customerKey);
      const result = brandFromCustomerData(customerData);
      if (result) {
        // Storage failures should not break rendering customer branding.
        try {
          const transformedData = {
            ...customerData,
            acceptedDomains: customerData?.allowed_email_domains || [],
            identityConfigs: customerData?.identity_configs || [],
          };
          localStorage.setItem(CUSTOMER_DATA_KEY, JSON.stringify(transformedData));
        } catch {
          // Non-fatal.
        }
        return result;
      }
    }

    // If API call fails, this might be admin access - fall back to OneDoor branding
    return { name: "One Door Platform", logoSrc: defaultCustomerLogo, alt: "ODP", product_alias: "ODP Cloud" };
    } catch {
    // API call failed - fall back to OneDoor branding
    return { name: "One Door Platform", logoSrc: defaultCustomerLogo, alt: "ODP", product_alias: "ODP Cloud" };
  }
}
